import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Button, Icon, Image, Input, InputOnChangeData } from 'semantic-ui-react'
import overview from '../../../../assets/svg/milestones_overview.svg'
import overview_active from '../../../../assets/svg/milestones_overview_selected.svg'
import tracker from '../../../../assets/svg/milestones_tracker.svg'
import tracker_active from '../../../../assets/svg/milestones_tracker_selected.svg'
import search from '../../../../assets/svg/search_icon.svg'
import { ContentHeader } from '../../../../components/Header/ContentHeader'
import { RootState } from '../../../../store/rootState'
import * as CostsActions from '../../actions/directAndPassTroughCosts'
import * as ProjectActions from '../../actions/projectActions'
import MilestoneModalComponent from '../../components/milestones/MilestoneModal'
import { _initialMilestone } from '../../reducers/directAndPassTroughCostsReducer'
import { getMilestonesWithTasksSelector } from '../../selectors/directAndPassTroughCostsSelector'
import { IMilestone } from '../../types/directAndPassTroughCosts'
import { IProject } from '../../types/project'


interface StateProps {
  milestones: IMilestone[]
  project: IProject
  milestoneView: 'overview' | 'unit_tracker'
}

interface DispatchProps {
  setMilestoneView: (view: 'overview' | 'unit_tracker') => void
  getMilestoneOfferDirectCosts: (prId: number, queryParams?: Record<string, string>) => void
  createMilestone: (prId: number, milestone: IMilestone) => Promise<IMilestone>
  getMilestoneUnitTracker: (prId: number, queryParams?: Record<string, string>) => void
}

type Props = StateProps & DispatchProps

const ProjectMilestonesHeader = (props: Props): JSX.Element => {
  const {
    project,
    milestones,
    milestoneView,
    getMilestoneOfferDirectCosts,
    setMilestoneView,
    createMilestone,
    getMilestoneUnitTracker
  } = props

  const [timer, setTimer] = useState<number>(0)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string | null>()
  const [openModal, setOpenModal] = useState(false)
  const [milestone, setMilestone] = useState(_initialMilestone)

  const prevProjectId = useRef(-1)

  useEffect(() => {
    const isProjectIdChanged = +project.id !== prevProjectId.current
    prevProjectId.current = +project.id
    if (isProjectIdChanged || searchQuery === '' || searchQuery) {
      if (searchQuery !== '' && searchQuery) {
        getMilestoneOfferDirectCosts(+project.id, { search: searchQuery })
      } else {
        getMilestoneOfferDirectCosts(+project.id)
      }
    }
  }, [project, getMilestoneOfferDirectCosts, searchQuery])

  const handleSetActiveTab = (tab: 'overview' | 'unit_tracker') => {
    if (milestoneView !== tab) {
      setMilestoneView(tab)
    }
  }

  const leftItems = () => {
    return (
      <div style={{ display: 'flex', gap: '8px' }}>
        <Image
          src={milestoneView === 'overview' ? overview_active : overview}
          onClick={() => handleSetActiveTab('overview')}
          style={{ width: '32px', cursor: 'pointer' }}
        />
        <Image
          src={milestoneView === 'unit_tracker' ? tracker_active : tracker}
          onClick={() => handleSetActiveTab('unit_tracker')}
          style={{ width: '32px', cursor: 'pointer' }}
        />
      </div>
    )
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    clearTimeout(timer)
    const _timer = window.setTimeout(() => {
      setSearchLoading(false)
    }, 300)
    setTimer(_timer)
    setSearchLoading(true)
    setSearchQuery(data.value)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const renderSearchIcon = () => {
    if (searchQuery === '' || !searchQuery) {
      return <Image src={search} style={{ left: '-100%', top: '-2%', padding: '0.5rem' }} />
    }
    return <Icon name='close' link={true} onClick={handleClearSearch} />
  }

  const renderAddTaskButton = () => {
    return (
      <div style={{ marginLeft: '8px' }}>
        <Button
          inverted
          size='small'
          color='teal'
          onClick={() => setOpenModal(true)}
          content='Add Milestone'
          icon={<Icon name='plus' />}
          style={{
            float: 'left',
            width: '135px',
            height: '32px',
            borderRadius: '.34rem',
            padding: 0,
            color: '#FFFFFF',
            backgroundColor: '#08D6A1',
            margin: 0
          }}
        />
      </div>
    )
  }

  const rightItems = () => {
    return (
      <React.Fragment>
        <Input
          loading={searchLoading}
          className='search-input-medium'
          size='small'
          style={{ width: '170px', height: '32px' }}
          icon={renderSearchIcon()}
          iconPosition='left'
          placeholder='Search'
          onChange={(e, data) => handleSearch(e, data)}
          value={searchQuery}
        />
        {renderAddTaskButton()}
      </React.Fragment>
    )
  }

  const handleCloseModal = () => {
    setMilestone(_initialMilestone)
    setOpenModal(false)
  }

  const handleCreateMilestone = async (milestone: IMilestone): Promise<IMilestone> => {
    const data = await createMilestone(+project.id, milestone).then((resp) => {
      getMilestoneUnitTracker(+project.id)
      handleCloseModal()
      return resp
    })
    return data
  }

  const renderContainer = () => {
    return (
      <ContentHeader
        id='projectActivitiesHeader'
        title={project.name}
        color='#08D6A1'
        subtitle={project.customer?.name ?? project.category}
        leftItems={leftItems()}
        rightItemsFirst={milestoneView === 'overview' ? rightItems() : undefined}
        backTitle='Milestones'
        backLocation='/projects'
      />
    )
  }

  return (
    <React.Fragment>
      {renderContainer()}
      <MilestoneModalComponent
        project={project}
        unassignedTasks={milestones.find(milestone => milestone.id === -1)?.tasks ?? []}
        open={openModal}
        milestone={milestone}
        saveMilestone={handleCreateMilestone}
        closeModal={handleCloseModal}
      />
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    milestones: getMilestonesWithTasksSelector(state),
    project: state.projectsState.project,
    milestoneView: state.projectsState.milestoneView
  }
}

const mapDispatchToProps = (dispatch: CostsActions.DirectAndPassTroughCostThunkDispatchType & ProjectActions.ProjectThunkDispatchType): DispatchProps => {
  return {
    setMilestoneView: (view) => dispatch(CostsActions.setMilestoneView(view)),
    getMilestoneOfferDirectCosts: (prId, queryParams) => dispatch(CostsActions.getMilestoneOfferDirectCosts(prId, queryParams)),
    createMilestone: (prId, milestone) => dispatch(CostsActions.createMilestone(prId, milestone)),
    getMilestoneUnitTracker: (prId, queryParams) => dispatch(CostsActions.getMilestoneUnitTracker(prId, queryParams)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectMilestonesHeader)
